import { useState } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import { LineItem } from '@pperform/pp-data-mappers';
import { lineItemTestID } from 'components/MerchantTrackingWidget/constants';
import {
  Wrapper,
  DetailsSection,
  ItemURL,
  ItemViewDetails,
  DescriptionSection,
  InfoSection,
  CenterSection,
  PriceQtyRow,
  ViewDetailsSection,
  TruncateTitleSection,
  ThumbnailSection,
  ListItemTitle,
  TruncateTextSection,
} from './ListItemCard.styles';
import {
  useFormatItemsCard,
  DescriptionTitle,
  DescriptionValue,
  Thumbnail,
} from '../ItemCommons';

interface ListItemCardProps {
  card: LineItem;
  showDetailButton: boolean;
}

const ListItemCard = ({
  card,
  showDetailButton,
}: ListItemCardProps): JSX.Element => {
  const { t } = useTranslation();

  const {
    productImageUrl,
    productName,
    productUrl,
    productDescription,
    productPrice,
    productQuantity,
    productReasonForReturn,
    productCustomReasonForReturn,
    additionalInformation,
    productID,
  } = useFormatItemsCard(card);

  const [isExpandDescription, setExpandDescription] = useState(false);

  const buttonAriaLabel = `${
    isExpandDescription
      ? t('LINE_ITEMS_HIDE_DETAILS')
      : t('LINE_ITEMS_VIEW_MORE_DETAILS')
  } ${productName}`;

  return (
    <Wrapper isExpandDescription={isExpandDescription}>
      {productImageUrl && (
        <ThumbnailSection>
          <Thumbnail
            isRounded
            width={80}
            height={80}
            src={productImageUrl}
            alt={productName}
          />
        </ThumbnailSection>
      )}
      <InfoSection>
        <DetailsSection>
          <TruncateTitleSection data-testid={`${lineItemTestID}-name`}>
            <ListItemTitle isShowFull={isExpandDescription}>
              {productName}
            </ListItemTitle>
          </TruncateTitleSection>
          <CenterSection>
            {productQuantity && (
              <PriceQtyRow
                gap={4}
                bottomMargin={0}
                data-testid={`${lineItemTestID}-quantity`}
              >
                <DescriptionValue>{productQuantity.value}</DescriptionValue>
              </PriceQtyRow>
            )}
            {productPrice && (
              <PriceQtyRow
                gap={4}
                bottomMargin={0}
                data-testid={`${lineItemTestID}-price`}
              >
                <DescriptionValue>{`${productPrice.label}: ${productPrice.value}`}</DescriptionValue>
              </PriceQtyRow>
            )}
            {productID && (
              <PriceQtyRow
                gap={4}
                bottomMargin={0}
                data-testid={`${lineItemTestID}-id`}
              >
                <DescriptionValue>{`${productID.label}: ${productID.value}`}</DescriptionValue>
              </PriceQtyRow>
            )}
          </CenterSection>
          {showDetailButton && (
            <ViewDetailsSection>
              <ItemViewDetails
                onClick={() => setExpandDescription(!isExpandDescription)}
                aria-label={buttonAriaLabel}
              >
                {isExpandDescription
                  ? t('LINE_ITEMS_HIDE_DETAILS')
                  : t('LINE_ITEMS_VIEW_MORE_DETAILS')}
              </ItemViewDetails>
            </ViewDetailsSection>
          )}
        </DetailsSection>
        {isExpandDescription && (
          <DescriptionSection direction="column" gap={16}>
            {productReasonForReturn && (
              <TruncateTextSection lines={2}>
                <DescriptionTitle>
                  {productReasonForReturn.label}:&nbsp;
                  <DescriptionValue isInline>
                    {productReasonForReturn.value}
                  </DescriptionValue>
                </DescriptionTitle>
              </TruncateTextSection>
            )}
            {productCustomReasonForReturn && (
              <TruncateTextSection lines={2}>
                <DescriptionTitle>
                  {productCustomReasonForReturn.label}:&nbsp;
                  <DescriptionValue isInline>
                    {productCustomReasonForReturn.value}
                  </DescriptionValue>
                </DescriptionTitle>
              </TruncateTextSection>
            )}
            {productDescription && (
              <DescriptionTitle>
                {productDescription.label}:&nbsp;
                <DescriptionValue isInline>
                  {productDescription.value}
                </DescriptionValue>
              </DescriptionTitle>
            )}
            {additionalInformation && (
              <DescriptionValue>{additionalInformation}</DescriptionValue>
            )}
            {productUrl && (
              <ItemURL
                href={productUrl.value}
                target="_blank"
                aria-label={`${productUrl.label} ${productName}`}
                data-testid={`${lineItemTestID}-url`}
              >
                {productUrl.label}
              </ItemURL>
            )}
          </DescriptionSection>
        )}
      </InfoSection>
    </Wrapper>
  );
};

export { ListItemCard };
